import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { getApiUrl } from '../config/api';

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalCustomers: 0,
    expiringDomains: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      console.log('Fetching dashboard data...');

      // Fetch dashboard statistics
      const [projectsRes, customersRes, hostingRes] = await Promise.all([
        axios.get(getApiUrl('/projects')),
        axios.get(getApiUrl('/customers')),
        axios.get(getApiUrl('/hosting-domains'))
      ]);

      // Ensure we have arrays for all data
      const projects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
      const customers = Array.isArray(customersRes.data) ? customersRes.data : [];
      const hostingDomains = Array.isArray(hostingRes.data) ? hostingRes.data : [];

      console.log('Dashboard API responses:', { projects, customers, hostingDomains });

      // Calculate statistics
      const activeProjects = projects.filter(p => p?.status === 'in_progress').length;
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const expiringDomains = hostingDomains.filter(h => {
        if (!h?.expiry_date) return false;
        const expiryDate = new Date(h.expiry_date);
        return expiryDate <= thirtyDaysFromNow && expiryDate >= now;
      }).length;

      setStats({
        totalProjects: projects.length,
        activeProjects: activeProjects,
        totalCustomers: customers.length,
        expiringDomains: expiringDomains
      });

      // Get recent projects (last 5)
      const recentProjects = projects
        .filter(p => p?.created_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentProjects(recentProjects);

      console.log('Dashboard data loaded successfully:', { stats: { totalProjects: projects.length, activeProjects, totalCustomers: customers.length, expiringDomains }, recentProjects });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setError('Không thể tải dữ liệu dashboard. Vui lòng thử lại.');

      // Set default values on error
      setStats({
        totalProjects: 0,
        activeProjects: 0,
        totalCustomers: 0,
        expiringDomains: 0
      });
      setRecentProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
      case 'in_progress': return 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
      case 'planning': return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
      case 'on_hold': return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Hoàn thành';
      case 'in_progress': return 'Đang thực hiện';
      case 'planning': return 'Lên kế hoạch';
      case 'on_hold': return 'Tạm dừng';
      default: return 'Không xác định';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-cyan-500/20 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-cyan-400 rounded-full animate-spin absolute top-0 left-0 border-t-transparent"></div>
          </div>
          <p className="mt-4 text-ocean-100/60">Đang tải dữ liệu dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
        <div className="flex items-center">
          <svg className="h-6 w-6 text-red-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <h3 className="text-red-400 font-medium">Lỗi tải dữ liệu</h3>
            <p className="text-red-400/70 text-sm mt-1">{error}</p>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                fetchDashboardData();
              }}
              className="mt-3 text-cyan-400 hover:text-cyan-300 text-sm underline"
            >
              Thử lại
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 rounded-2xl border border-cyan-500/20 p-6 backdrop-blur-xl">
        <h1 className="text-2xl font-bold text-white">
          Chào mừng, <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">{user?.full_name || 'User'}</span>!
        </h1>
        <p className="text-ocean-100/60 mt-1">
          Đây là tổng quan về các dự án và hoạt động của bạn
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Projects */}
        <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-cyan-500/20 p-6 hover:border-cyan-500/40 transition-all group">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl group-hover:shadow-glow-cyan/30 transition-all">
              <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-100/60">Tổng dự án</p>
              <p className="text-3xl font-bold text-white">{stats.totalProjects}</p>
            </div>
          </div>
        </div>

        {/* Active Projects */}
        <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-emerald-500/20 p-6 hover:border-emerald-500/40 transition-all group">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-xl group-hover:shadow-glow-cyan/30 transition-all">
              <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-100/60">Đang thực hiện</p>
              <p className="text-3xl font-bold text-white">{stats.activeProjects}</p>
            </div>
          </div>
        </div>

        {/* Total Customers */}
        <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-purple-500/20 p-6 hover:border-purple-500/40 transition-all group">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl group-hover:shadow-glow-cyan/30 transition-all">
              <svg className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-100/60">Khách hàng</p>
              <p className="text-3xl font-bold text-white">{stats.totalCustomers}</p>
            </div>
          </div>
        </div>

        {/* Expiring Domains */}
        <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-red-500/20 p-6 hover:border-red-500/40 transition-all group">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-xl group-hover:shadow-glow-cyan/30 transition-all">
              <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-100/60">Domain sắp hết hạn</p>
              <p className="text-3xl font-bold text-white">{stats.expiringDomains}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-cyan-500/20 overflow-hidden">
        <div className="px-6 py-4 border-b border-cyan-500/10">
          <h3 className="text-lg font-semibold text-white">Dự án gần đây</h3>
        </div>
        <div className="divide-y divide-cyan-500/10">
          {recentProjects.length > 0 ? recentProjects.map((project) => (
            <div key={project.id} className="px-6 py-4 hover:bg-cyan-500/5 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-white">{project?.name || 'Dự án không có tên'}</h4>
                  <p className="text-sm text-ocean-100/60">{project?.customer?.company_name || 'Khách hàng không xác định'}</p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project?.status)}`}>
                  {getStatusText(project?.status)}
                </span>
              </div>
            </div>
          )) : (
            <div className="px-6 py-12 text-center">
              <svg className="w-12 h-12 text-ocean-100/30 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-ocean-100/50">Chưa có dự án nào</p>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-cyan-500/10">
          <a href="/projects" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1">
            Xem tất cả dự án
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
